/**
 * AffTrackTimeframeControl (#802 AC6/AC7)
 *
 * The timeframe control on a drawn AFF track's layer-drawer entry. Offers the
 * same options as the map track duration — 24h, 48h, 72h, or a custom start and
 * end — but scoped to one track: the request carries that track's existing id,
 * so it is redrawn in place and no other drawn track is affected.
 *
 * A fixed duration applies as soon as it is picked. A custom range waits for
 * Apply: half a range is neither a duration nor a range, and firing on the
 * first field would redraw over a window the user never asked for.
 */

import React, { useState } from 'react';
import { Layer } from '../../../types/layer';
import {
  AFF_TRACK_HOURS_OPTIONS,
  AFF_TRACK_MAP_HOURS,
} from '../../../components/explorers/affExplorer/AffAircraftDetailModal';
import { dispatchAffTrackRetime, type AffTrackSpan } from '../../../components/explorers/affExplorer/affTrackEvent';
import './AffTrackTimeframeControl.scss';

interface AffTrackTimeframeControlProps {
  layer: Layer;
}

export const AffTrackTimeframeControl: React.FC<AffTrackTimeframeControlProps> = ({ layer }) => {
  const drawnSpan = (layer.metadata?.span ?? {}) as AffTrackSpan;

  // Open on the span the track was actually drawn with, so the control reports
  // the truth rather than a default that may not match what is on the map.
  const [hours, setHours] = useState<number>(drawnSpan.hours ?? AFF_TRACK_MAP_HOURS);
  const [isCustom, setIsCustom] = useState<boolean>(!!(drawnSpan.start && drawnSpan.end));
  const [start, setStart] = useState<string>(drawnSpan.start ?? '');
  const [end, setEnd] = useState<string>(drawnSpan.end ?? '');

  const esn = String(layer.metadata?.esn ?? '');
  const groupName = `aff-track-span-${layer.id}`;

  // A track drawn by registration is redrawn by registration (#866): the
  // aircraft across every tracker it carried, not the one ESN it started from.
  const request = (span: AffTrackSpan) =>
    dispatchAffTrackRetime({
      trackId: layer.id,
      esn,
      aircraftName: layer.name,
      span: drawnSpan.registration ? { ...span, registration: drawnSpan.registration } : span,
    });

  const chooseHours = (value: number) => {
    setIsCustom(false);
    setHours(value);
    request({ hours: value });
  };

  return (
    <div className="aff-track-timeframe">
      <div className="aff-track-timeframe__label">Timeframe</div>

      <div className="aff-track-timeframe__options">
        {AFF_TRACK_HOURS_OPTIONS.map((option) => (
          <label key={option} className="aff-track-timeframe__option">
            <input
              type="radio"
              name={groupName}
              checked={!isCustom && hours === option}
              onChange={() => chooseHours(option)}
            />
            <span>{option} h</span>
          </label>
        ))}

        <label className="aff-track-timeframe__option">
          <input
            type="radio"
            name={groupName}
            checked={isCustom}
            onChange={() => setIsCustom(true)}
          />
          <span>Custom</span>
        </label>
      </div>

      {isCustom && (
        <div className="aff-track-timeframe__range">
          {/* Wall-clock NWT whatever the browser zone: buildTrackUrl converts
              it with nwtLocalDateTimeToIso before it is sent (#866 U6). */}
          <label>
            Start (NWT)
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </label>
          <label>
            End (NWT)
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </label>
          <button
            type="button"
            disabled={!start || !end}
            onClick={() => request({ start, end })}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

export default AffTrackTimeframeControl;
